import { useState, useCallback, useMemo } from 'react'

interface FormField {
  value: string
  state: 'error' | null
  valid: boolean
  message: string
}

type ValidationPredicate = (value: string, formState: FormState) => boolean

type ValidationMap = Record<
  string,
  {
    errors: Record<string, ValidationPredicate>
  }
>

interface FormState {
  fields: Record<string, FormField>
  valid: boolean
}

interface UseFormOptions {
  initialFields: Record<string, string>
  validators: ValidationMap
  onSubmit: (values: Record<string, string>) => void | Promise<void>
}

export const useForm = ({ initialFields, validators, onSubmit }: UseFormOptions) => {
  const [formState, setFormState] = useState<FormState>(() => {
    const fields: Record<string, FormField> = {}
    for (const [key, value] of Object.entries(initialFields)) {
      fields[key] = { value, state: null, valid: true, message: '' }
    }
    return { fields, valid: true }
  })

  const validateField = useCallback(
    (name: string, currentFields: Record<string, FormField>): FormField => {
      const field = currentFields[name]
      if (!field) return { value: '', state: null, valid: true, message: '' }
      const fieldValidators = validators[name]?.errors || {}
      const fakeState: FormState = { fields: currentFields, valid: true }
      for (const [message, isInvalid] of Object.entries(fieldValidators)) {
        if (isInvalid(field.value, fakeState)) {
          return { ...field, state: 'error', valid: false, message }
        }
      }
      return { ...field, state: null, valid: true, message: '' }
    },
    [validators]
  )

  const validateAll = useCallback(
    (fields: Record<string, FormField>): FormState => {
      const validated: Record<string, FormField> = {}
      for (const name of Object.keys(fields)) {
        validated[name] = validateField(name, fields)
      }
      const valid = Object.values(validated).every((f) => f.valid)
      return { fields: validated, valid }
    },
    [validateField]
  )

  const handleFieldChange = useCallback(
    (name: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value
      setFormState((prev) => {
        const prevField = prev.fields[name]
        const fields: Record<string, FormField> = {
          ...prev.fields,
          [name]: { value, state: prevField?.state ?? null, valid: prevField?.valid ?? true, message: prevField?.message ?? '' },
        }
        return validateAll(fields)
      })
    },
    [validateAll]
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setFormState((prev) => {
        const validated = validateAll(prev.fields)
        if (validated.valid) {
          const values: Record<string, string> = {}
          for (const [key, field] of Object.entries(validated.fields)) {
            values[key] = field.value
          }
          Promise.resolve(onSubmit(values))
        }
        return validated
      })
    },
    [validateAll, onSubmit]
  )

  const fieldValue = useCallback((name: string) => formState.fields[name]?.value ?? '', [formState])
  const fieldMessage = useCallback((name: string) => formState.fields[name]?.message ?? '', [formState])
  const validationState = useCallback(
    (name: string): 'error' | null => formState.fields[name]?.state ?? null,
    [formState]
  )
  const formInvalid = useMemo(
    () => !formState.valid || Object.values(formState.fields).some((f) => !f.valid),
    [formState]
  )

  return {
    formState,
    setFormState,
    handleFieldChange,
    handleSubmit,
    fieldValue,
    fieldMessage,
    validationState,
    formInvalid,
  }
}
